import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { LayoutHeader as Header } from '@/layouts';
import { Footer } from '@/components/Footer';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Tag } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { openWhatsApp } from '@/lib/whatsapp';

const BannerVehicles = () => {
  const { config, vehicles } = useAppData();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [bannerVehicles, setBannerVehicles] = useState<any[]>([]);
  const [bannerTitle, setBannerTitle] = useState('Veículos em Promoção');
  const { colors } = useTheme();

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;
  
  useEffect(() => {
    // Get vehicle IDs from URL
    const idsParam = searchParams.get('ids');
    const titleParam = searchParams.get('title');

    if (titleParam) {
      setBannerTitle(decodeURIComponent(titleParam));
    }

    if (idsParam) {
      const vehicleIds = idsParam.split(',');
      const filtered = vehicles.filter(vehicle => 
        vehicleIds.includes(vehicle.id.toString())
      );
      setBannerVehicles(filtered);
    } else {
      // No IDs provided, redirect to vehicles page
      navigate('/estoque');
    }
  }, [searchParams, vehicles, navigate]);

  useEffect(() => {
    document.title = `${bannerTitle} - ${config.name}`;
  }, [bannerTitle, config.name]);

  return (
    <div className="min-h-screen bg-muted/30">
      <Header config={config} />

      <div className="container mx-auto px-4 pt-36 pb-16">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Tag className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold whitespace-nowrap" style={{ color: titleColor }}>
                {bannerTitle}
              </h1>
              <p className="text-lg" style={{ color: textColor }}>
                {bannerVehicles.length} {bannerVehicles.length === 1 ? 'veículo' : 'veículos'} em destaque
              </p>
            </div>
          </div>
        </div>

        {/* Vehicles Grid */}
        {bannerVehicles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {bannerVehicles.map((vehicle) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                whatsappUrl={config.stores[0].whatsapp?.ios}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Nenhum veículo encontrado
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Os veículos desta promoção não estão mais disponíveis.
            </p>
            <Button onClick={() => navigate('/estoque')}>
              Ver Todos os Veículos
            </Button>
          </div>
        )}

        {/* CTA Section */}
        {bannerVehicles.length > 0 && (
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-2xl p-8 border border-primary/20">
              <h2 className="text-2xl font-bold text-foreground mb-3">
                Gostou de algum veículo?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Entre em contato conosco para mais informações, agendar test-drive ou simular financiamento.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Button
                  onClick={() => openWhatsApp(config.stores[0].whatsapp?.ios)}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  size="lg"
                >
                  Falar no WhatsApp
                </Button>
                <Button
                  onClick={() => navigate('/estoque')}
                  variant="outline"
                  size="lg"
                >
                  Ver Todos os Veículos
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer config={config} />
    </div>
  );
};

export default BannerVehicles;

